import { createContext, HTMLProps, ReactNode, useContext } from "react";

import { messageSales } from "@/api/endpoints/contact";
import CheckIcon from "@/assets/icons/check.svg";
import ContactSalesModal from "@/containers/ContactSalesModal";
import { useMessages } from "@/containers/Messages/MessageContext";
import { useToggle } from "@/hooks/useToggle";
import { useUser } from "@/hooks/useUser";
import { FrontendGrowthPlan } from "@/mappings/growth_plans";
import { User } from "@/types/User";
import { cx } from "@/utils";
import Button, { ButtonProps } from "@components/Button";
import Typography, { TypographyProps } from "@components/Typography";

import styles from "./PlanCard.module.css";

const PlanCardContext = createContext<FrontendGrowthPlan>(
  {} as FrontendGrowthPlan
);

export const usePlanCard = () => useContext(PlanCardContext);

export const Root = ({
  plan,
  children,
}: {
  plan: FrontendGrowthPlan;
  children: ReactNode;
}) => (
  <PlanCardContext.Provider value={plan}>
    <div className={cx(styles.root, styles[plan.type])}>{children}</div>
  </PlanCardContext.Provider>
);

export const Name = ({ children, ...props }: TypographyProps) => {
  const { name, accent } = usePlanCard();

  return (
    <Typography
      style={{ color: accent }}
      className={styles.name}
      variant="h2"
      margin="none"
      {...props}
    >
      {name} {children}
    </Typography>
  );
};

export const Price = ({ className, ...props }: TypographyProps) => {
  const { price_string, price_extra } = usePlanCard();

  return (
    <Typography
      margin="none"
      variant="h1"
      className={cx(styles.price, className)}
      {...props}
    >
      {price_string}

      <Typography variant="body-2">{price_extra}</Typography>
    </Typography>
  );
};

export const FeatureTitle = (props: TypographyProps) => {
  const { feature_title } = usePlanCard();
  return (
    <Typography margin="none" variant="subtitle-1" {...props}>
      {feature_title}
    </Typography>
  );
};

const ListItem = ({ ...props }: HTMLProps<HTMLSpanElement>) => (
  <li className={styles.item}>
    <CheckIcon strokeWidth={3} width="1rem" />
    <span {...props} />
  </li>
);

export const Features = ({ className }: HTMLProps<HTMLUListElement>) => {
  const { features } = usePlanCard();

  return (
    <ul className={cx(styles.list, className)}>
      {features.map((feature, index) => {
        return (
          <ListItem
            key={index}
            dangerouslySetInnerHTML={{ __html: feature.name }}
          />
        );
      })}
    </ul>
  );
};

export const CtaBasic = (props: ButtonProps) => {
  const { cta } = usePlanCard();

  return (
    <Button className={styles.cta} {...props}>
      {cta}
    </Button>
  );
};

export const CtaCustom = ({
  onSent,
  planName,
  ...props
}: ButtonProps & { onSent?: () => void; planName: string }) => {
  const { cta } = usePlanCard();
  const { data: user } = useUser();

  const { name, email } = (user?.data as User) || {};

  const { danger } = useMessages();
  const [modalOpen, toggleModalOpen] = useToggle(false);

  const contactSales = async () => {
    return messageSales({
      name,
      email,
      type: "upsale",
      subject: `${planName}-lead`,
    }).then((res) => {
      if (res.ok) {
        toggleModalOpen();
        onSent?.();
      } else {
        danger({
          title: L.CONTACT_SALES_MESSAGE_ERROR,
        });
      }
    });
  };

  return (
    <>
      <ContactSalesModal show={modalOpen} onHide={contactSales} />
      <Button className={styles.cta} {...props} onClick={toggleModalOpen}>
        {cta}
      </Button>
    </>
  );
};
